import React from 'react';
import { Heart, Coffee, Users, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import './Support.css';

const Support = () => {
  // Replace these with your actual links once you set up accounts
  const donationLinks = {
    kofi: '#', // Replace with: https://ko-fi.com/yourusername
    buymeacoffee: '#', // Replace with: https://buymeacoffee.com/yourusername
    patreon: '#' // Replace with: https://patreon.com/yourusername
  };

  const platforms = [
    {
      name: 'Ko-fi',
      icon: <Coffee size={32} />,
      description: 'One-time or monthly support',
      fees: '0% platform fees',
      best: 'Best for simple donations',
      link: donationLinks.kofi,
      color: '#13C3FF'
    },
    {
      name: 'Buy Me a Coffee',
      icon: <Coffee size={32} />,
      description: 'Support with a virtual coffee',
      fees: '5% platform fee',
      best: 'Popular & user-friendly',
      link: donationLinks.buymeacoffee,
      color: '#FFDD00'
    },
    {
      name: 'Patreon',
      icon: <Users size={32} />,
      description: 'Monthly membership tiers',
      fees: '5-12% platform fee',
      best: 'Best for ongoing community',
      link: donationLinks.patreon,
      color: '#FF424D'
    }
  ];

  const impactAreas = [
    {
      icon: <Sparkles size={28} />,
      title: 'Keep Content Free',
      description: 'All philosophy, practices, and resources remain accessible to everyone.'
    },
    {
      icon: <Heart size={28} />,
      title: 'Ad-Free Experience',
      description: 'No advertisements interrupting your contemplative journey.'
    },
    {
      icon: <Users size={28} />,
      title: 'Grow the Community',
      description: 'Support virtual Cosmergent Circles and create more resources.'
    }
  ];

  return (
    <div className="support-page">
      <SEO
        title="Support Cosmergence"
        description="Help keep Cosmergence ad-free and accessible. Support our philosophy of eternal resonance through voluntary contributions."
        keywords="support cosmergence, donate, philosophy support, ad-free content, voluntary contribution"
        canonicalUrl="/support"
      />

      {/* Hero Section */}
      <section className="support-hero cosmic-bg">
        <div className="container">
          <div className="support-hero-content">
            <Heart size={48} className="text-gold support-heart" />
            <h1 className="heading-1 text-center">Support Cosmergence</h1>
            <p className="body-large text-center" style={{maxWidth: '700px', margin: '24px auto 0'}}>
              Help us keep this philosophy freely accessible while maintaining an ad-free, contemplative space.
            </p>
          </div>
        </div>
      </section>

      {/* Why Support Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">Why Your Support Matters</h2>
          <div className="impact-grid">
            {impactAreas.map((area, index) => (
              <div key={index} className="impact-card card">
                <div className="impact-icon text-gold">
                  {area.icon}
                </div>
                <h3 className="heading-3">{area.title}</h3>
                <p className="body-regular">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Options - Coming Soon */}
      <section className="section-padding-small cosmic-bg">
        <div className="container">
          <h2 className="heading-2 text-center mb-4">Choose Your Way to Support</h2>
          <p className="body-regular text-center" style={{maxWidth: '600px', margin: '0 auto 48px', color: 'var(--text-muted)'}}>
            We're setting up donation options to keep Cosmergence freely accessible.
          </p>
          
          <div className="coming-soon-container">
            <div className="coming-soon-card card">
              <Sparkles size={48} className="text-gold coming-soon-icon" />
              <h3 className="heading-2">Donation Options Coming Soon</h3>
              <p className="body-large" style={{maxWidth: '500px', margin: '24px auto 0'}}>
                We're preparing simple and secure ways for you to support Cosmergence. 
                Check back soon for donation links.
              </p>
              <p className="body-regular" style={{marginTop: '32px', color: 'var(--text-muted)'}}>
                In the meantime, you can support by sharing this philosophy with others who might resonate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Comparison - Commented out for now */}
      {/* 
      <section className="section-padding">
        <div className="container">
          <div className="comparison-section">
            <h2 className="heading-2 text-center mb-4">Not Sure Which to Choose?</h2>
            <div className="comparison-content">
              <div className="comparison-item">
                <h4 className="heading-3 text-gold">For One-Time Donations</h4>
                <p className="body-regular">Choose <strong>Ko-fi</strong> or <strong>Buy Me a Coffee</strong>. Both are simple, quick, and perfect for spontaneous support.</p>
              </div>
              
              <div className="comparison-item">
                <h4 className="heading-3 text-gold">For Monthly Support</h4>
                <p className="body-regular">Choose <strong>Patreon</strong> if you want ongoing membership benefits, or <strong>Ko-fi</strong> for simple recurring donations.</p>
              </div>
              
              <div className="comparison-item">
                <h4 className="heading-3 text-gold">Lowest Fees</h4>
                <p className="body-regular"><strong>Ko-fi</strong> has 0% platform fees, meaning more of your contribution goes directly to supporting Cosmergence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Gratitude Section */}
      <section className="section-padding-small cosmic-bg">
        <div className="container">
          <div className="gratitude-section">
            <div className="quote-callout" style={{border: 'none', background: 'transparent'}}>
              <p style={{fontSize: '20px', fontStyle: 'italic'}}>
                "To those who choose to support: your resonance amplifies the cosmic song. 
                Thank you for helping keep this philosophy accessible to all who seek it."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Other Ways to Support */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">Other Ways to Support</h2>
          <div className="other-ways-grid">
            <div className="other-way-card">
              <h3 className="heading-3">Share the Philosophy</h3>
              <p className="body-regular">
                Tell others about Cosmergence. Share pages with those who might find meaning here.
              </p>
            </div>
            
            <div className="other-way-card">
              <h3 className="heading-3">Start a Cosmergent Circle</h3>
              <p className="body-regular">
                Gather others for reflection, music, and scientific awe. Build local community.
              </p>
            </div>
            
            <div className="other-way-card">
              <h3 className="heading-3">Provide Feedback</h3>
              <p className="body-regular">
                Your insights help us grow. Contact us with suggestions and reflections.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;